import { defaultLayoutGrid, type LayoutGrid, uniformLayoutGrid } from "@cva/shared";
import {
  Check,
  Eye,
  EyeOff,
  LayoutGrid as LayoutGridIcon,
  Link2,
  Lock,
  LockOpen,
  Plus,
  Ruler,
  Trash2,
  Unlink2,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import {
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSeparator,
  DropdownTrigger,
} from "../components/ui/dropdown";
import { cn } from "../lib/utils";
import { GRID_COLOR, GUIDE_COLOR, requestNewGuide, useLayoutAids } from "./Rulers";
import { useEditor, useEditorApi } from "./store";
import { IconButton, NumberField, Section } from "./ui";
import { useVisionMode, VisionSubmenu } from "./VisionSimulation";
import { clearGuides, layoutUnits, setLayoutGrid, useViewPrefs } from "./view";

/**
 * Settings of the layout grid ("Satzspiegel") and the guides: a section of
 * the properties panel (nothing selected), a dialog and the view menu.
 */

function Swatch({ color, dashed }: { color: string; dashed?: boolean }) {
  return (
    <span
      className="inline-block h-0 w-3 shrink-0 align-middle"
      style={{ borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}` }}
    />
  );
}

/** What the lines on the canvas mean. */
export function LayoutLegend({ print }: { print: boolean }) {
  const items: [ReactNode, string][] = [
    [<Swatch key="g" color={GUIDE_COLOR} />, "Hilfslinien"],
    [<Swatch key="r" color={GRID_COLOR} />, "Satzspiegel, Spalten und Zeilen"],
    ...(print
      ? ([
          [<Swatch key="t" color="rgb(239 68 68)" />, "Endformat"],
          [<Swatch key="s" color="rgb(14 165 233)" dashed />, "Sicherheitsabstand"],
        ] as [ReactNode, string][])
      : []),
  ];
  return (
    <ul className="space-y-0.5 text-xs text-zinc-500">
      {items.map(([swatch, label]) => (
        <li key={label} className="flex items-center gap-2">
          {swatch}
          {label}
        </li>
      ))}
    </ul>
  );
}

const MARGINS = [
  ["top", "Oben"],
  ["bottom", "Unten"],
  ["left", "Links"],
  ["right", "Rechts"],
] as const;

/** Margins, columns and rows of the document's grid. */
function LayoutGridFields() {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const grid = doc.layoutGrid;
  const units = layoutUnits(doc);
  const print = doc.format.print;
  const safePx = print ? units.fromDisplay(print.safeMm) : 0;
  const set = (next: LayoutGrid, merge: string) => setLayoutGrid(store, next, { merge });
  if (!grid) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-zinc-500">
          Ränder, Spalten und Zeilen als Raster für das Layout. Elemente rasten an den Linien ein;
          im Export ist das Raster nicht zu sehen.
        </p>
        <Button
          size="sm"
          variant="secondary"
          className="w-full"
          onClick={() => setLayoutGrid(store, defaultLayoutGrid(doc))}
        >
          <LayoutGridIcon /> Satzspiegel anlegen
        </Button>
      </div>
    );
  }
  const linked = grid.linked ?? false;
  const length = (key: string, label: string, value: number, onChange: (px: number) => void) => (
    <NumberField
      key={key}
      label={label}
      value={units.toDisplay(value)}
      unit={units.unit}
      precision={units.precision}
      step={units.step}
      min={0}
      max={units.toDisplay(20_000)}
      onChange={(display) => onChange(Math.max(0, units.fromDisplay(display)))}
      // The next change of the field is a new undo step.
      onCommit={() => store.setState({ lastMergeKey: null })}
    />
  );
  const count = (label: string, value: number, onChange: (value: number) => void) => (
    <NumberField
      label={label}
      value={value}
      min={1}
      max={24}
      onChange={(next) => onChange(Math.max(1, Math.min(24, Math.round(next))))}
    />
  );
  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-500">Ränder</span>
        <IconButton
          label={linked ? "Ränder einzeln einstellen" : "Alle Ränder gleich"}
          active={linked}
          onClick={() =>
            set(
              linked
                ? { ...grid, linked: false }
                : {
                    ...grid,
                    linked: true,
                    margins: {
                      top: grid.margins.top,
                      right: grid.margins.top,
                      bottom: grid.margins.top,
                      left: grid.margins.top,
                    },
                  },
              "grid-link",
            )
          }
        >
          {linked ? <Link2 /> : <Unlink2 />}
        </IconButton>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {MARGINS.map(([side, label]) =>
          length(side, label, grid.margins[side], (px) =>
            set(
              {
                ...grid,
                margins: linked
                  ? { top: px, right: px, bottom: px, left: px }
                  : { ...grid.margins, [side]: px },
              },
              linked ? "grid-margins" : `grid-margin-${side}`,
            ),
          ),
        )}
      </div>
      <span className="block text-xs text-zinc-500">Spalten und Spaltenabstand</span>
      <div className="grid grid-cols-2 gap-2">
        {count("Anzahl", grid.columns, (columns) => set({ ...grid, columns }, "grid-columns"))}
        {length("column-gutter", "Abstand", grid.columnGutter, (columnGutter) =>
          set({ ...grid, columnGutter }, "grid-column-gutter"),
        )}
      </div>
      <span className="block text-xs text-zinc-500">Zeilen und Zeilenabstand</span>
      <div className="grid grid-cols-2 gap-2">
        {count("Anzahl", grid.rows, (rows) => set({ ...grid, rows }, "grid-rows"))}
        {length("row-gutter", "Abstand", grid.rowGutter, (rowGutter) =>
          set({ ...grid, rowGutter }, "grid-row-gutter"),
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {print ? (
          <Button
            size="sm"
            variant="ghost"
            title={`Alle Ränder auf den Sicherheitsabstand (${print.safeMm.toLocaleString("de-DE")} mm)`}
            onClick={() =>
              set(
                {
                  ...uniformLayoutGrid(safePx),
                  columns: grid.columns,
                  columnGutter: grid.columnGutter,
                  rows: grid.rows,
                  rowGutter: grid.rowGutter,
                },
                "grid-safe",
              )
            }
          >
            Wie Sicherheitsabstand
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          className="text-red-600"
          onClick={() => setLayoutGrid(store, null)}
        >
          <Trash2 /> Entfernen
        </Button>
      </div>
    </div>
  );
}

/** Properties panel (nothing selected): layout grid and guides of the document. */
export function LayoutSection() {
  const store = useEditorApi();
  const aids = useLayoutAids();
  const doc = useEditor((s) => s.doc);
  const prefs = useViewPrefs();
  if (!aids) return null;
  const guides = doc.guides?.length ?? 0;
  return (
    <>
      <Section
        title="Layout-Raster"
        actions={
          doc.layoutGrid ? (
            <IconButton
              label={prefs.grid ? "Raster ausblenden" : "Raster einblenden"}
              shortcut="⇧G"
              onClick={() => prefs.toggle("grid")}
            >
              {prefs.grid ? <Eye /> : <EyeOff />}
            </IconButton>
          ) : null
        }
      >
        <LayoutGridFields />
      </Section>
      <Section
        title="Hilfslinien"
        actions={
          guides > 0 ? (
            <div className="flex">
              <IconButton
                label={prefs.guidesLocked ? "Hilfslinien entsperren" : "Hilfslinien sperren"}
                active={prefs.guidesLocked}
                onClick={() => prefs.toggle("guidesLocked")}
              >
                {prefs.guidesLocked ? <Lock /> : <LockOpen />}
              </IconButton>
              <IconButton
                label={prefs.guides ? "Hilfslinien ausblenden" : "Hilfslinien einblenden"}
                shortcut="⇧H"
                onClick={() => prefs.toggle("guides")}
              >
                {prefs.guides ? <Eye /> : <EyeOff />}
              </IconButton>
            </div>
          ) : null
        }
      >
        <p className="text-xs text-zinc-500">
          {guides > 0
            ? `${guides} ${guides === 1 ? "Hilfslinie" : "Hilfslinien"} – für alle Seiten. `
            : ""}
          Aus einem Lineal auf die Seite ziehen; zurück ins Lineal ziehen löscht sie. Doppelklick
          für eine genaue Position.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" onClick={() => requestNewGuide()}>
            <Plus /> Hinzufügen …
          </Button>
          {guides > 0 ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600"
              onClick={() => clearGuides(store)}
            >
              <Trash2 /> Alle löschen
            </Button>
          ) : null}
        </div>
        <LayoutLegend print={Boolean(doc.format.print)} />
      </Section>
    </>
  );
}

function MenuCheck({ checked }: { checked: boolean }) {
  return <Check className={checked ? undefined : "invisible"} />;
}

function Shortcut({ children }: { children: ReactNode }) {
  return <span className="ml-auto pl-6 text-xs text-zinc-400">{children}</span>;
}

/** "Ansicht" in the editor's header: rulers, guides, grid (designs) and the vision preview. */
export function ViewMenu() {
  const store = useEditorApi();
  const aids = useLayoutAids();
  const readOnly = useEditor((s) => s.readOnly);
  const guides = useEditor((s) => s.doc.guides?.length ?? 0);
  const print = useEditor((s) => Boolean(s.doc.format.print));
  const prefs = useViewPrefs();
  const vision = useVisionMode();
  const [gridOpen, setGridOpen] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Ansicht"
            title="Ansicht: Lineale, Hilfslinien, Sehschwächen-Vorschau"
            className={cn(vision !== "none" && "bg-brand-50 text-brand-700")}
          >
            <Ruler />
          </Button>
        </DropdownTrigger>
        <DropdownContent className="min-w-64">
          <DropdownLabel>Ansicht</DropdownLabel>
          {aids ? (
            <>
              <DropdownItem onSelect={() => prefs.toggle("rulers")}>
                <MenuCheck checked={prefs.rulers} /> Lineale <Shortcut>⇧R</Shortcut>
              </DropdownItem>
              <DropdownItem onSelect={() => prefs.toggle("guides")}>
                <MenuCheck checked={prefs.guides} /> Hilfslinien <Shortcut>⇧H</Shortcut>
              </DropdownItem>
              <DropdownItem onSelect={() => prefs.toggle("guidesLocked")}>
                <MenuCheck checked={prefs.guidesLocked} /> Hilfslinien sperren
              </DropdownItem>
              <DropdownItem onSelect={() => prefs.toggle("grid")}>
                <MenuCheck checked={prefs.grid} /> Layout-Raster <Shortcut>⇧G</Shortcut>
              </DropdownItem>
              {readOnly ? null : (
                <>
                  <DropdownSeparator />
                  <DropdownItem onSelect={() => requestNewGuide()}>
                    <Plus /> Hilfslinie hinzufügen …
                  </DropdownItem>
                  <DropdownItem onSelect={() => setGridOpen(true)}>
                    <LayoutGridIcon /> Layout-Raster einstellen …
                  </DropdownItem>
                  <DropdownItem danger disabled={guides === 0} onSelect={() => clearGuides(store)}>
                    <Trash2 /> Alle Hilfslinien löschen
                  </DropdownItem>
                </>
              )}
              <DropdownSeparator />
              <div className="px-2 py-1.5">
                <LayoutLegend print={print} />
              </div>
              <DropdownSeparator />
            </>
          ) : null}
          <VisionSubmenu />
        </DropdownContent>
      </DropdownMenu>
      <Dialog open={gridOpen} onOpenChange={setGridOpen}>
        <DialogContent
          title="Layout-Raster"
          description="Satzspiegel mit Rändern, Spalten und Zeilen – gilt für alle Seiten und ist nur im Editor zu sehen."
        >
          <LayoutGridFields />
          <DialogFooter>
            <Button onClick={() => setGridOpen(false)}>Fertig</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
